import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { ListTodo, CheckCircle2, Circle, Clock, AlertCircle } from 'lucide-react';
import api from '@/lib/api';

const STATUS_ICON = {
  'Not Started': Circle,
  'In Progress': Clock,
  'Blocked': AlertCircle,
  'Done': CheckCircle2,
  'Completed': CheckCircle2,
};

const STATUS_COLOR = {
  'Not Started': 'text-muted-foreground',
  'In Progress': 'text-blue-500',
  'Blocked': 'text-amber-500',
  'Done': 'text-emerald-500',
  'Completed': 'text-emerald-500',
};

/**
 * Small pill shown on a meeting note card when at least one task was spawned
 * from it. Clicking opens a popover listing the tasks with status + assignee.
 */
export default function MeetingNoteTodosBadge({ note }) {
  const count = note?.todo_count || 0;
  const [tasks, setTasks] = useState(null);
  const [loading, setLoading] = useState(false);

  if (count === 0) return null;

  const loadOnce = async () => {
    if (tasks !== null || loading) return;
    setLoading(true);
    try {
      const r = await api.get(`/meeting-notes/${note.id}/todos`);
      setTasks(r.data || []);
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popover onOpenChange={(o) => o && loadOnce()}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex"
          data-testid={`note-todos-badge-${note.id}`}
        >
          <Badge className="text-xs bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25 border border-emerald-500/30 cursor-pointer transition-colors">
            <ListTodo className="h-2.5 w-2.5 mr-0.5" />
            {count} to-do{count === 1 ? '' : 's'}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2 bg-card border-border" data-testid={`note-todos-popover-${note.id}`}>
        <p className="text-xs text-muted-foreground mb-2 px-1">
          Tasks created from this meeting note
        </p>
        {loading ? (
          <p className="text-xs text-muted-foreground px-1 py-2">Loading…</p>
        ) : (tasks || []).length === 0 ? (
          <p className="text-xs text-muted-foreground px-1 py-2">No tasks.</p>
        ) : (
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {tasks.map(t => {
              const Icon = STATUS_ICON[t.status] || Circle;
              const color = STATUS_COLOR[t.status] || 'text-muted-foreground';
              return (
                <div key={t.id} className="px-2 py-1.5 rounded hover:bg-secondary/40 text-xs" data-testid={`note-todo-${t.id}`}>
                  <div className="flex items-start gap-2">
                    <Icon className={`h-3 w-3 mt-0.5 shrink-0 ${color}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{t.title}</p>
                      <div className="flex items-center gap-2 flex-wrap mt-0.5">
                        {t.project_label && (
                          <span className="text-xs text-primary font-mono truncate">{t.project_label}</span>
                        )}
                        {t.project_status === 'Archived' && (
                          <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-600 px-1">Archived</Badge>
                        )}
                        <span className={`text-xs ${color}`}>{t.status}</span>
                        {t.assignee_name && (
                          <span className="text-xs text-muted-foreground">· {t.assignee_name}</span>
                        )}
                        {t.due_date && (
                          <span className="text-xs text-muted-foreground">· due {t.due_date}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
